import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import CatalogSkeleton from 'components/CatalogSkeleton'
import Button from 'components/Form/Button'
import Select from 'components/Form/Select'
import FavoriteCard from 'components/Favorites/FavoriteCard'
import FavoriteListRow from 'components/Favorites/FavoriteListRow'
import FolderSidebar from 'components/Favorites/FolderSidebar'
import { openFavoriteItem, buildFavoriteDragId, buildFolderDragId, UNFILED } from 'components/Favorites/utils'
import {
  DragIndicatorOutlined,
  DriveFileMoveOutlined,
  FolderOutlined,
  GridViewOutlined,
  Inventory2Outlined,
  ViewListOutlined,
} from '@mui/icons-material'
import { useAuthLayout } from 'context/AuthLayoutContext'
import useI18n from 'hooks/useI18n'
import useFavoritesWorkspace from './useFavoritesWorkspace'
import {
  ContentGrid,
  ContentList,
  DragGhostBadge,
  DragGhostCard,
  DragGhostStack,
  DragGhostThumb,
  DragGhostTitle,
  DragTrail,
  EmptyState,
  EmptyText,
  EmptyTitle,
  FavoritesShell,
  MainHeader,
  MainHeadingBlock,
  MainPanel,
  MainSubtitle,
  MainTitle,
  SectionEyebrow,
  SelectionPill,
  SelectSlot,
  Toolbar,
  ToolbarGroup,
  ViewButton,
  ViewSwitch,
  WorkspaceFrame,
} from './styled'

const getActivatorPoint = (event) => {
  if (!event) return null
  if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
    return { x: event.clientX, y: event.clientY }
  }
  if (event.touches?.[0]) {
    return { x: event.touches[0].clientX, y: event.touches[0].clientY }
  }
  return null
}

const keepOverlayNearPointer = ({ activatorEvent, activeNodeRect, transform }) => {
  const activatorPoint = getActivatorPoint(activatorEvent)
  if (!activatorPoint || !activeNodeRect) return transform

  return {
    ...transform,
    x: transform.x + activatorPoint.x - activeNodeRect.left - 18,
    y: transform.y + activatorPoint.y - activeNodeRect.top - 18,
  }
}

export default function Favorites() {
  const { t } = useI18n()
  const { setLayoutProps } = useAuthLayout()
  const history = useHistory()
  const location = useLocation()
  const {
    workspace,
    loading,
    busyState,
    loadWorkspace,
    removeFavorite,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFavoritesToFolder,
    reorderFolders,
    reorderFavoritesInFolder,
    isFavoriteBusy,
    isFolderBusy,
  } = useFavoritesWorkspace()

  const [viewMode, setViewMode] = useState('gallery')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [editingFolderId, setEditingFolderId] = useState(null)
  const [editingFolderName, setEditingFolderName] = useState('')
  const [selectedFavorites, setSelectedFavorites] = useState([])
  const [moveTarget, setMoveTarget] = useState(UNFILED)
  const [activeDragFavoriteIds, setActiveDragFavoriteIds] = useState([])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 160, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const currentFolderParam = searchParams.get('folder') || UNFILED

  const setActiveFolder = useCallback((folderId) => {
    const params = new URLSearchParams(location.search)
    params.set('folder', folderId == null ? UNFILED : `${folderId}`)
    history.replace({
      pathname: '/favorites',
      search: `?${params.toString()}`,
    })
  }, [history, location.search])

  const folderExists = useMemo(() => (
    currentFolderParam === UNFILED ||
    workspace.folders.some((folder) => `${folder.id}` === `${currentFolderParam}`)
  ), [currentFolderParam, workspace.folders])

  const activeFolderId = useMemo(() => (
    currentFolderParam === UNFILED ? null : Number(currentFolderParam)
  ), [currentFolderParam])

  const activeFolder = useMemo(() => (
    activeFolderId == null
      ? {
        id: null,
        name: t('favorites_unfiled'),
        count: workspace.unfiledCount,
      }
      : workspace.folders.find((folder) => folder.id === activeFolderId)
  ), [activeFolderId, t, workspace.folders, workspace.unfiledCount])

  const visibleItems = useMemo(() => workspace.items
    .filter((item) => (item?.folderId ?? null) === activeFolderId)
    .sort((a, b) => {
      const posA = Number.isFinite(a?.position) ? a.position : Number.MAX_SAFE_INTEGER
      const posB = Number.isFinite(b?.position) ? b.position : Number.MAX_SAFE_INTEGER
      if (posA !== posB) return posA - posB
      return (b?.favoriteId || 0) - (a?.favoriteId || 0)
    }), [activeFolderId, workspace.items])

  const folderOptions = useMemo(() => ([
    { id: UNFILED, title: t('favorites_unfiled') },
    ...workspace.folders.map((folder) => ({ id: folder.id, title: folder.name })),
  ]), [t, workspace.folders])

  const selectedCount = selectedFavorites.length
  const activeDragItems = useMemo(() => (
    activeDragFavoriteIds
      .map((favoriteId) => visibleItems.find((item) => item.favoriteId === favoriteId))
      .filter(Boolean)
  ), [activeDragFavoriteIds, visibleItems])

  useEffect(() => {
    loadWorkspace()
  }, [loadWorkspace])

  useEffect(() => {
    setLayoutProps({})
  }, [setLayoutProps])

  useEffect(() => {
    if (!folderExists && !loading) {
      setActiveFolder(null)
    }
  }, [folderExists, loading, setActiveFolder])

  useEffect(() => {
    setSelectedFavorites((prev) => prev.filter((favoriteId) => visibleItems.some((item) => item.favoriteId === favoriteId)))
  }, [visibleItems])

  const handleOpenItem = useCallback((item) => {
    openFavoriteItem(history, item)
  }, [history])

  const toggleSelection = useCallback((favoriteId) => {
    setSelectedFavorites((prev) => (
      prev.includes(favoriteId)
        ? prev.filter((id) => id !== favoriteId)
        : [...prev, favoriteId]
    ))
  }, [])

  const handleCreateFolder = useCallback(async () => {
    const created = await createFolder(newFolderName)
    if (!created) return

    setCreatingFolder(false)
    setNewFolderName('')
    setActiveFolder(created?.id)
  }, [createFolder, newFolderName, setActiveFolder])

  const handleRenameFolder = useCallback(async (folderId) => {
    const renamed = await renameFolder(folderId, editingFolderName)
    if (!renamed) return

    setEditingFolderId(null)
    setEditingFolderName('')
  }, [editingFolderName, renameFolder])

  const handleDeleteFolder = useCallback(async (folderId) => {
    const confirmed = window.confirm(t('favorites_delete_folder_confirm'))
    if (!confirmed) return

    const deleted = await deleteFolder(folderId)
    if (!deleted) return

    if (`${activeFolderId}` === `${folderId}`) {
      setActiveFolder(null)
    }
  }, [activeFolderId, deleteFolder, setActiveFolder, t])

  const handleBulkMove = useCallback(async () => {
    if (!selectedFavorites.length) return

    const targetFolderId = moveTarget === UNFILED ? null : Number(moveTarget)
    const moved = await moveFavoritesToFolder(selectedFavorites, targetFolderId)
    if (!moved) return

    setSelectedFavorites([])
    if (`${currentFolderParam}` !== `${moveTarget}`) {
      setActiveFolder(targetFolderId)
    }
  }, [currentFolderParam, moveFavoritesToFolder, moveTarget, selectedFavorites, setActiveFolder])

  const handleDragStart = useCallback(({ active }) => {
    if (active?.data?.current?.type !== 'item') return

    const activeFavoriteId = active?.data?.current?.favoriteId
    setActiveDragFavoriteIds(
      selectedFavorites.includes(activeFavoriteId)
        ? selectedFavorites
        : [activeFavoriteId]
    )
  }, [selectedFavorites])

  const handleDragEnd = useCallback(async ({ active, over }) => {
    setActiveDragFavoriteIds([])
    if (!over || active.id === over.id) return

    const activeType = active?.data?.current?.type
    const overType = over?.data?.current?.type

    if (activeType === 'folder' && overType === 'folder') {
      const oldIndex = workspace.folders.findIndex((folder) => buildFolderDragId(folder.id) === active.id)
      const newIndex = workspace.folders.findIndex((folder) => buildFolderDragId(folder.id) === over.id)

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

      const nextOrder = arrayMove(workspace.folders, oldIndex, newIndex).map((folder) => folder.id)
      await reorderFolders(nextOrder)
      return
    }

    if (activeType !== 'item') return

    const activeFavoriteId = active?.data?.current?.favoriteId
    const activeItem = visibleItems.find((item) => item.favoriteId === activeFavoriteId)
    if (!activeItem) return

    if (overType === 'item') {
      const oldIndex = visibleItems.findIndex((item) => buildFavoriteDragId(item.favoriteId) === active.id)
      const newIndex = visibleItems.findIndex((item) => buildFavoriteDragId(item.favoriteId) === over.id)

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

      const nextOrder = arrayMove(visibleItems, oldIndex, newIndex).map((item) => item.favoriteId)
      await reorderFavoritesInFolder(activeFolderId, nextOrder)
      return
    }

    if (overType === 'folder') {
      const targetFolderId = over?.data?.current?.folderId ?? null
      const movingFavoriteIds = selectedFavorites.includes(activeFavoriteId)
        ? selectedFavorites
        : [activeFavoriteId]
      const movableFavoriteIds = movingFavoriteIds.filter((favoriteId) => {
        const item = visibleItems.find((visibleItem) => visibleItem.favoriteId === favoriteId)
        return item && (item?.folderId ?? null) !== targetFolderId
      })

      if (!movableFavoriteIds.length) return

      const moved = await moveFavoritesToFolder(movableFavoriteIds, targetFolderId)
      if (!moved) return

      if (targetFolderId !== activeFolderId) {
        setSelectedFavorites((prev) => prev.filter((id) => !movableFavoriteIds.includes(id)))
      }
    }
  }, [
    activeFolderId,
    moveFavoritesToFolder,
    reorderFavoritesInFolder,
    reorderFolders,
    selectedFavorites,
    visibleItems,
    workspace.folders,
  ])

  if (loading) {
    return <CatalogSkeleton showFilters={false} />
  }

  return (
    <FavoritesShell>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragCancel={() => setActiveDragFavoriteIds([])}
        onDragEnd={handleDragEnd}
      >
        <WorkspaceFrame>
          <FolderSidebar
            t={t}
            workspace={workspace}
            activeFolderId={activeFolderId}
            creatingFolder={creatingFolder}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            setCreatingFolder={setCreatingFolder}
            handleCreateFolder={handleCreateFolder}
            editingFolderId={editingFolderId}
            editingFolderName={editingFolderName}
            setEditingFolderId={setEditingFolderId}
            setEditingFolderName={setEditingFolderName}
            handleRenameFolder={handleRenameFolder}
            handleDeleteFolder={handleDeleteFolder}
            setActiveFolder={setActiveFolder}
            isFolderBusy={isFolderBusy}
            busyState={busyState}
          />

          <MainPanel>
            <MainHeader>
              <MainHeadingBlock>
                <SectionEyebrow>{t('favorites_current_view')}</SectionEyebrow>
                <MainTitle>{activeFolder?.name || t('favorites_unfiled')}</MainTitle>
                <MainSubtitle>
                  {activeFolderId == null
                    ? t('favorites_unfiled_description')
                    : t('favorites_folder_description')}
                </MainSubtitle>
              </MainHeadingBlock>

              <ToolbarGroup>
                <SelectionPill>
                  <Inventory2Outlined fontSize="small" />
                  {visibleItems.length} {t('results')}
                </SelectionPill>
                <ViewSwitch>
                  <ViewButton type="button" active={viewMode === 'gallery'} onClick={() => setViewMode('gallery')}>
                    <GridViewOutlined fontSize="small" /> {t('favorites_grid')}
                  </ViewButton>
                  <ViewButton type="button" active={viewMode === 'list'} onClick={() => setViewMode('list')}>
                    <ViewListOutlined fontSize="small" /> {t('favorites_list')}
                  </ViewButton>
                </ViewSwitch>
              </ToolbarGroup>
            </MainHeader>

            <Toolbar>
              <ToolbarGroup>
                <SelectionPill>
                  <DragIndicatorOutlined fontSize="small" />
                  {t('favorites_drag_hint')}
                </SelectionPill>
                {selectedCount ? (
                  <SelectionPill>
                    {selectedCount} {t('favorites_selected')}
                  </SelectionPill>
                ) : null}
              </ToolbarGroup>

              {selectedCount ? (
                <ToolbarGroup>
                  <SelectSlot>
                    <Select
                      id="favorites-move-target"
                      placeholder={t('favorites_move_to')}
                      options={folderOptions}
                      value={`${moveTarget}`}
                      onChange={setMoveTarget}
                      small
                      secondary
                    />
                  </SelectSlot>
                  <Button
                    small
                    color="primary"
                    width="fit-content"
                    startIcon={<DriveFileMoveOutlined />}
                    onClick={handleBulkMove}
                    disabled={!selectedCount || `${moveTarget}` === `${currentFolderParam}`}
                  >
                    {t('favorites_move_selected')}
                  </Button>
                </ToolbarGroup>
              ) : null}
            </Toolbar>

            {!visibleItems.length ? (
              <EmptyState>
                <FolderOutlined sx={{ fontSize: 52 }} />
                <EmptyTitle>{t('no_favorites_message')}</EmptyTitle>
                <EmptyText>
                  {activeFolderId == null
                    ? t('favorites_empty_unfiled')
                    : t('favorites_empty_folder')}
                </EmptyText>
              </EmptyState>
            ) : (
              <SortableContext
                items={visibleItems.map((item) => buildFavoriteDragId(item.favoriteId))}
                strategy={viewMode === 'gallery' ? rectSortingStrategy : verticalListSortingStrategy}
              >
                {viewMode === 'gallery' ? (
                  <ContentGrid>
                    {visibleItems.map((item) => (
                      <FavoriteCard
                        key={item.favoriteId}
                        item={item}
                        selected={selectedFavorites.includes(item.favoriteId)}
                        onSelect={toggleSelection}
                        onOpen={handleOpenItem}
                        onRemove={removeFavorite}
                        busy={isFavoriteBusy(item.favoriteId)}
                      />
                    ))}
                  </ContentGrid>
                ) : (
                  <ContentList>
                    {visibleItems.map((item) => (
                      <FavoriteListRow
                        key={item.favoriteId}
                        item={item}
                        selected={selectedFavorites.includes(item.favoriteId)}
                        onSelect={toggleSelection}
                        onOpen={handleOpenItem}
                        onRemove={removeFavorite}
                        busy={isFavoriteBusy(item.favoriteId)}
                      />
                    ))}
                  </ContentList>
                )}
              </SortableContext>
            )}
          </MainPanel>
        </WorkspaceFrame>

        <DragOverlay adjustScale={false} modifiers={[keepOverlayNearPointer]}>
          {activeDragItems.length ? (
            <DragGhostStack>
              <DragTrail />
              <DragTrail />
              <DragGhostCard>
                <DragGhostThumb
                  src={activeDragItems[0]?.image?.url || activeDragItems[0]?.cover || '/images/image.png'}
                  alt={activeDragItems[0]?.title}
                  draggable={false}
                />
                <DragGhostTitle>{activeDragItems[0]?.title}</DragGhostTitle>
                <DragGhostBadge>{activeDragItems.length}</DragGhostBadge>
              </DragGhostCard>
            </DragGhostStack>
          ) : null}
        </DragOverlay>
      </DndContext>
    </FavoritesShell>
  )
}
